import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import Logo from './Logo';
import GoogleTranslateWidget from './GoogleTranslateWidget';

const Navigation: React.FC = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Navigation items based on Figma design
  const navigation = [
    { name: t('nav.home'), href: '/', current: location.pathname === '/' },
    { name: t('nav.about_us'), href: '/about', current: location.pathname === '/about' },
    { name: t('nav.how_it_works'), href: '/how-it-works', current: location.pathname === '/how-it-works' },
    { name: t('nav.pricing'), href: '/pricing', current: location.pathname === '/pricing' },
    { name: t('nav.faqs'), href: '/faqs', current: location.pathname === '/faqs' },
    { name: t('nav.contact_us'), href: '/contact', current: location.pathname === '/contact' },
  ];

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 bg-white ${
        isScrolled 
          ? 'shadow-[0px_0px_2px_rgba(23,26,31,0.12),0px_2px_5px_rgba(23,26,31,0.09)]' 
          : 'shadow-[0px_0px_2px_rgba(23,26,31,0.12),0px_2px_5px_rgba(23,26,31,0.09)]'
      }`}
      style={{ height: '64px' }}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16" style={{ height: '64px' }}>
          {/* Logo Section */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 group"
          >
            <Logo size="md" className="transition-transform duration-200 group-hover:scale-105" />
            <div className="flex flex-col justify-center">
              <span className="font-opensans text-[40px] leading-[44px] font-bold text-brand-blue whitespace-nowrap" style={{ fontFamily: "'Open Sans', sans-serif" }}>
                Master Ji
              </span>
              <span className="font-manrope text-[11px] leading-[14px] text-brand-yellow -mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t('home.hero.tagline')}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center space-x-1" style={{ right: '229px', top: 'calc(50% - 24px/2)' }}>
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-[4px] transition-all duration-200 ${
                  item.current
                    ? 'text-brand-yellow font-bold'
                    : 'text-text-secondary hover:text-brand-blue hover:bg-gray-50'
                }`}
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                  lineHeight: '22px',
                  fontWeight: item.current ? '700' : '400',
                  color: item.current ? '#FFC857' : '#424856'
                }}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right Side - Auth & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Widget */}
            <GoogleTranslateWidget />
            
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-600">
                  {user.name}
                </span>
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-brand-blue px-3 py-2 rounded-lg hover:bg-gray-50 transition-all duration-200">
                    <div className="w-8 h-8 bg-brand-blue/10 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    {user.role === 'admin' && (
                      <Link 
                        to="/admin/dashboard" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-blue"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        {t('nav.admin_dashboard')}
                      </Link>
                    )}
                    {user.role === 'tailor' && (
                      <Link 
                        to="/business/dashboard" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-blue"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {t('nav.business_dashboard')}
                      </Link>
                    )}
                    {user.role === 'customer' && (
                      <Link 
                        to="/user/dashboard" 
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-blue"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t('nav.my_account')}
                      </Link>
                    )}
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={logout}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('nav.sign_out')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-brand-blue px-4 py-2 text-sm font-medium transition-all duration-200"
                >
                  {t('nav.login')}
                </button>
                <button
                  onClick={() => navigate('/auth?mode=signup')}
                  className="bg-brand-yellow transition-all duration-200"
                  style={{
                    width: '100px',
                    height: '36px',
                    borderRadius: '18px',
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: '14px',
                    lineHeight: '22px',
                    fontWeight: '400',
                    color: '#704B00',
                    textAlign: 'center'
                  }}
                >
                  {t('nav.get_started')}
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-brand-blue hover:bg-gray-50 transition-all duration-200"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.href}
                    className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                      item.current
                        ? 'text-brand-blue bg-brand-blue/10'
                        : 'text-gray-700 hover:text-brand-blue hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Auth Section */}
              <div className="pt-4 mt-4 border-t border-gray-100">
                {user ? (
                  <div className="space-y-2">
                    <div className="px-4 py-3 bg-gray-50 rounded-xl">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin/dashboard"
                        className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-brand-blue hover:bg-gray-50 rounded-xl"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('nav.admin_dashboard')}
                      </Link>
                    )}
                    {user.role === 'tailor' && (
                      <Link
                        to="/business/dashboard"
                        className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-brand-blue hover:bg-gray-50 rounded-xl"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('nav.business_dashboard')}
                      </Link>
                    )}
                    {user.role === 'customer' && (
                      <Link
                        to="/user/dashboard"
                        className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-brand-blue hover:bg-gray-50 rounded-xl"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {t('nav.my_account')}
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-xl"
                    >
                      {t('nav.sign_out')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        navigate('/login');
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-base font-medium text-gray-700 hover:text-brand-blue border border-gray-200 rounded-xl transition-all duration-200 text-center"
                    >
                      {t('nav.login')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/auth?mode=signup');
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-base font-semibold bg-brand-yellow text-brand-blue rounded-xl transition-all duration-200 text-center shadow-sm"
                    >
                      {t('nav.get_started')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
